export const STORAGE_KEY = "motekhassesan.expert-error-tracker.v2";
export const AUTH_KEY = "motekhassesan.expert-error-tracker.auth";
export const USERS_STORAGE_KEY = "motekhassesan.expert-error-tracker.users";

export const PAGE_SIZE = 5;

export const PERMISSIONS = {
  addFeedback: "ثبت بازخورد (مثبت و منفی)",
  addPerson: "افزودن کارشناس به گروه",
  update: "ویرایش گروه‌ها، کارشناسان و بازخوردها",
  delete: "حذف گروه‌ها، کارشناسان و بازخوردها",
  applyBuff: "اعمال باف و انتقال به تاریخچه",
  manageGroups: "ایجاد گروه جدید",
  settings: "تنظیمات آستانهٔ باف",
  manageUsers: "مدیریت کاربران، دسترسی‌ها و تخصیص گروه"
};

export const EMPTY_PERMISSIONS = Object.fromEntries(
  Object.keys(PERMISSIONS).map((key) => [key, false])
);

function defaultUsers() {
  return [
    {
      id: "seed-admin", username: "admin", password: "admin123", name: "مدیر کل",
      permissions: Object.fromEntries(Object.keys(PERMISSIONS).map((key) => [key, true])),
      accessGroups: "all"
    },
    {
      id: "seed-supervisor", username: "supervisor", password: "super123", name: "سرپرست",
      permissions: { ...EMPTY_PERMISSIONS, addFeedback: true, addPerson: true },
      accessGroups: []
    }
  ];
}

function migrateUser(u) {
  const { role, label, ...rest } = u;
  const prev = u.permissions || {};
  const wasAdmin = role === "super_admin" || !!prev.manageUsers;

  const next = { ...EMPTY_PERMISSIONS, ...prev };

  if (!("update" in prev) && prev.delete) next.update = true;
  if (!("applyBuff" in prev) && wasAdmin) next.applyBuff = true;
  if (!("manageGroups" in prev) && wasAdmin) next.manageGroups = true;
  if (!("addPerson" in prev) && (prev.addFeedback || wasAdmin)) next.addPerson = true;

  if (wasAdmin && !u.permissions) {
    Object.keys(PERMISSIONS).forEach((key) => { next[key] = true; });
  }

  return {
    ...rest,
    permissions: Object.fromEntries(
      Object.keys(PERMISSIONS).map((key) => [key, !!next[key]])
    ),
    accessGroups: u.accessGroups ?? (next.manageUsers ? "all" : [])
  };
}

export function loadUsers() {
  try {
    const raw = localStorage.getItem(USERS_STORAGE_KEY);
    if (!raw) {
      const seeded = defaultUsers();
      localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) throw new Error("لیست کاربران نامعتبر است");
    const migrated = parsed.map(migrateUser);
    if (migrated.some((u, i) => u !== parsed[i])) saveUsers(migrated);
    return migrated;
  } catch (err) {
    console.error(err);
    const seeded = defaultUsers();
    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
}

function saveUsers(list) {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(list));
}

function normalizePermissions(permissions) {
  return Object.fromEntries(
    Object.keys(PERMISSIONS).map((key) => [key, !!permissions?.[key]])
  );
}

export function addUser({ username, password, name, permissions, accessGroups }) {
  const users = loadUsers();
  const cleanUsername = username.trim();
  if (users.some(u => u.username.toLowerCase() === cleanUsername.toLowerCase())) return { error: "این نام کاربری قبلاً ثبت شده است." };
  const perms = normalizePermissions(permissions);
  const next = [...users, {
    id: uid(), username: cleanUsername, password, name: name || "",
    permissions: perms,
    accessGroups: perms.manageUsers ? "all" : (Array.isArray(accessGroups) ? accessGroups : [])
  }];
  saveUsers(next);
  return { ok: true };
}

export function updateUser(id, { username, password, name, permissions, accessGroups }) {
  const users = loadUsers();
  const cleanUsername = username.trim();
  if (users.some(u => u.id !== id && u.username.toLowerCase() === cleanUsername.toLowerCase())) return { error: "این نام کاربری قبلاً ثبت شده است." };
  const perms = normalizePermissions(permissions);
  const next = users.map(u => u.id === id ? {
    ...u, username: cleanUsername, password, name: name || "",
    permissions: perms,
    accessGroups: perms.manageUsers ? "all" : (Array.isArray(accessGroups) ? accessGroups : [])
  } : u);
  saveUsers(next);
  return { ok: true };
}

export function userHasGroupAccess(user, groupId) {
  if (!user) return false;
  if (user.permissions?.manageUsers) return true;
  if (user.accessGroups === "all") return true;
  if (Array.isArray(user.accessGroups) && user.accessGroups.map(String).includes(String(groupId))) return true;
  if (Array.isArray(user.groups) && user.groups.some((g) => String(g.id) === String(groupId))) return true;
  return false;
}

export function deleteUser(id) {
  const users = loadUsers();
  const target = users.find(u => u.id === id);
  if (!target) return { error: "کاربر پیدا نشد." };
  const remainingManagers = users.filter(u => u.permissions?.manageUsers && u.id !== id);
  if (target.permissions?.manageUsers && remainingManagers.length === 0) {
    return { error: "باید حداقل یک کاربر با دسترسی «مدیریت کاربران» در سیستم باقی بماند." };
  }
  saveUsers(users.filter(u => u.id !== id));
  return { ok: true };
}

export function findUser(username, password) {
  const users = loadUsers();
  const clean = username.trim().toLowerCase();
  return users.find(u => u.username.toLowerCase() === clean && u.password === password) || null;
}

export const DEFAULT_SETTINGS = {
  negativeThreshold: 5,
  positiveThreshold: 5
};

export const INITIAL_DATA = {
  groups: [
    { id: "support", name: "پشتیبانی", system: true, created_at: "2026-09-23T00:00:00" },
    { id: "layout", name: "صفحه آرایی", system: true, created_at: "2026-09-23T00:00:00" },
    { id: "editing", name: "ویراستاری", system: true, created_at: "2026-09-23T00:00:00" },
    { id: "sales", name: "فروش", system: true, created_at: "2026-09-23T00:00:00" },
    { id: "marketing", name: "مارکتینگ", system: true, created_at: "2026-09-23T00:00:00" },
    { id: "printing", name: "چاپخانه", system: true, created_at: "2026-09-23T00:00:00" }
  ],
  people: [
    { id: "20260901175126-355790902", name: "افسانه عبدالرحمنی فرسنگی", created_at: "2026-09-01T17:51:26", group_id: "layout" },
    { id: "20260901175044-627170375", name: "علی عبادی فر", created_at: "2026-09-01T17:50:44", group_id: "layout" },
    { id: "20260831093540-401915404", name: "سحر جان‌پرستی", created_at: "2026-08-31T09:35:40", group_id: "layout" },
    { id: "20260824104532-547392697", name: "اسماء ثبتی", created_at: "2026-08-24T10:45:32", group_id: "layout" },
    { id: "20260818135812-672067239", name: "مرتضی زنده بودی", created_at: "2026-08-18T13:58:12", group_id: "layout" },
    { id: "20260817140126-944566848", name: "عارفه ثباتی فر", created_at: "2026-08-17T14:01:26", group_id: "layout" }
  ],
  appliedBuffs: [],
  feedbacks: [
    { id: "20260817140238-308532327", people_id: "20260817140126-944566848", type: "negative", image: null, caption: "شابک دوره در کتاب صوفی مست درج نشده\nدر تاریخ 15 مرداد در گفتگو میزیتو شابک دوره ارسال شده اما روی فایل اعمال نشده\nبا توجه به عبارت جلد اول حتی اگر شابک دوره ارسال نشده بود از طرف صفحه آرا باید کار توقف بخورد و شابک دوره از پشتیبان اخذ شود.", created_at: "2026-08-17T14:02:38" },
    { id: "20260818135842-90448516", people_id: "20260818135812-672067239", type: "negative", image: null, caption: "نخواندن گزارشات قرارداد نشر. باعث شد نام ویراستار در کتاب درج بشه و برای مسئول مجوز بره و در مجوز تعلیق بشه.", created_at: "2026-08-18T13:58:42" },
    { id: "20260820144610-580489726", people_id: "20260817140126-944566848", type: "negative", image: null, caption: "بهار احمدی (قوانین معنوی موفقیت براساس جهان‌بینی مولانا (راهنمایی برای تحقق‌بخشیدن به اهداف و آرزوها))\n\nنوبت چاپ در شناسنامه خورده اول 1405 با یک فاصله اضافی بین سال و پرانتزش. این خطا به علت عدم دقت کافی پیش آمده و در فرصتی که برای چک مجدد خروجی به وجود آمد هم اصلاح صورت نگرفت و خطا شمرده شد.", created_at: "2026-08-20T14:46:10" },
    { id: "20260824104616-954910358", people_id: "20260824104532-547392697", type: "negative", image: null, caption: "آوردن نام ویراستار در کتاب حرف‌هایی از جنس نگفتن با وجود گزارش واضح و درخواست ویراستار برای عدم درج اسم در کتاب", created_at: "2026-08-24T10:46:16" },
    { id: "20260824104643-28781994", people_id: "20260824104532-547392697", type: "negative", image: null, caption: "کارکتر دایرکشن اشتباه در کتاب حرف‌هایی از جنس نگفتن که باعث شده بود انگلیسی‌هاش چپه نوشته بشه.", created_at: "2026-08-24T10:46:43" },
    { id: "20260825164231-586144449", people_id: "20260824104532-547392697", type: "negative", image: null, caption: "محسن آتش دست - ساحت دوم - شابک اشتباه", created_at: "2026-08-25T16:42:31" },
    { id: "20260831093600-919359364", people_id: "20260831093540-401915404", type: "negative", image: null, caption: "عدم رعایت first line indent در رمان وفا", created_at: "2026-08-31T09:36:00" },
    { id: "20260901173752-650185672", people_id: "20260817140126-944566848", type: "negative", image: null, caption: "آوردن نام ایمان شکیبا در کتاب محسن نیکخواه!", created_at: "2026-09-01T17:37:52" },
    { id: "20260901175115-992058203", people_id: "20260901175044-627170375", type: "negative", image: null, caption: "درج نام احمد بهزادی رفیع با وجود قید شدن در گزارشات که نمیخوان باشه اسمشون.", created_at: "2026-09-01T17:51:15" },
    { id: "20260901175258-422007586", people_id: "20260901175126-355790902", type: "negative", image: null, caption: "درج نام ویراستار در دو کتاب با وجود قید شدن در گزارشات", created_at: "2026-09-01T17:52:58" },
    { id: "20260906115627-685480344", people_id: "20260824104532-547392697", type: "negative", image: null, caption: "سال نشر در کتاب \"هنر ساختن یک رابطۀ سالم راهنمایی علمی و کاربردی برای ایجاد و حفظ روابط عاطفی سالم و پایدار\" خورده بود 1403 و چک نکرده ارسال شده بود.", created_at: "2026-09-06T11:56:27" },
    { id: "20260906115705-338704332", people_id: "20260824104532-547392697", type: "negative", image: null, caption: "در کتاب \"هنر ساختن یک رابطۀ سالم راهنمایی علمی و کاربردی برای ایجاد و حفظ روابط عاطفی سالم و پایدار\" همه منابع ایتالیک شده بودند.", created_at: "2026-09-06T11:57:05" },
    { id: "20260906115749-724021282", people_id: "20260817140126-944566848", type: "negative", image: null, caption: "در کتاب \"میراث آتش و عشق\" 3 4 صفحه با فونت متفاوت کار شده بود و همینطوری ارسال کردند برای نهایی.", created_at: "2026-09-06T11:57:49" }
  ],
  settings: { ...DEFAULT_SETTINGS }
};

export function uid() {
  const stamp = new Date().toISOString().replace(/[-:TZ.]/g, "");
  return stamp + "-" + Math.floor(Math.random() * 900000000 + 100000000);
}

export function now() {
  return new Date().toISOString().slice(0, 19);
}

export function buffCount(count, threshold) {
  const t = Number(threshold) > 0 ? Number(threshold) : 5;
  return Math.floor(Math.max(0, Number(count) || 0) / t);
}

export function fa(value) {
  try { return Number(value || 0).toLocaleString("fa-IR"); }
  catch (_) { return String(value || 0); }
}

export function fdateParts(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: String(value || "—"), time: "" };
  try {
    const date = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
      year: "numeric", month: "2-digit", day: "2-digit"
    }).format(d);
    const time = new Intl.DateTimeFormat("fa-IR", {
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    }).format(d);
    return { date, time };
  } catch (_) {
    return { date: String(value || "—"), time: "" };
  }
}

function defaultGroups() {
  return JSON.parse(JSON.stringify(INITIAL_DATA.groups));
}

function migrateLegacyState() {
  try {
    const raw = localStorage.getItem("motekhassesan.expert-error-tracker.v1");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.people)) return null;
    const feedbacks = Array.isArray(parsed.errors)
      ? parsed.errors.map(e => ({ ...e, type: "negative", image: null }))
      : [];
    return { groups: parsed.groups, people: parsed.people, feedbacks, settings: { ...DEFAULT_SETTINGS } };
  } catch (_) {
    return null;
  }
}

export function normalizeState(candidate) {
  const normalized = {
    groups: Array.isArray(candidate?.groups) ? candidate.groups : defaultGroups(),
    people: Array.isArray(candidate?.people) ? candidate.people : [],
    feedbacks: Array.isArray(candidate?.feedbacks) ? candidate.feedbacks : [],
    appliedBuffs: Array.isArray(candidate?.appliedBuffs) ? candidate.appliedBuffs : [],
    settings: {
      negativeThreshold: Number(candidate?.settings?.negativeThreshold) > 0 ? Number(candidate.settings.negativeThreshold) : DEFAULT_SETTINGS.negativeThreshold,
      positiveThreshold: Number(candidate?.settings?.positiveThreshold) > 0 ? Number(candidate.settings.positiveThreshold) : DEFAULT_SETTINGS.positiveThreshold
    }
  };

  const known = new Set(normalized.groups.map(g => String(g.id)));
  defaultGroups().forEach(group => {
    if (!known.has(String(group.id))) normalized.groups.push(group);
  });

  normalized.people = normalized.people.map(p => ({
    ...p,
    group_id: known.has(String(p.group_id)) || normalized.groups.some(g => String(g.id) === String(p.group_id))
      ? p.group_id
      : "layout"
  }));

  normalized.feedbacks = normalized.feedbacks.map(f => ({
    ...f,
    type: f.type === "positive" ? "positive" : "negative",
    image: f.image || null,
    applied_buff_id: f.applied_buff_id || null
  }));

  normalized.appliedBuffs = normalized.appliedBuffs.map(b => ({
    ...b,
    feedback_ids: Array.isArray(b.feedback_ids) ? b.feedback_ids : [],
    note: b.note || ""
  }));

  return normalized;
}

export function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const migrated = migrateLegacyState();
      const seeded = normalizeState(migrated || JSON.parse(JSON.stringify(INITIAL_DATA)));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.people) || !Array.isArray(parsed.feedbacks)) throw new Error("ساختار داده نامعتبر است");
    return normalizeState(parsed);
  } catch (err) {
    console.error(err);
    return normalizeState(JSON.parse(JSON.stringify(INITIAL_DATA)));
  }
}

export function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function fileToCompressedDataUrl(file, maxDim = 900, quality = 0.72) {
  return new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("خواندن فایل ناموفق بود"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("فایل تصویر معتبر نیست"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round(height * (maxDim / width)); width = maxDim; }
          else { width = Math.round(width * (maxDim / height)); height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export function toEnglishDigits(str) {
  return String(str)
    .replace(/[۰-۹]/g, (ch) => "۰۱۲۳۴۵۶۷۸۹".indexOf(ch))
    .replace(/[٠-٩]/g, (ch) => "٠١٢٣٤٥٦٧٨٩".indexOf(ch));
}

export function toPersianDigits(str) {
  return String(str).replace(/[0-9]/g, (ch) => "۰۱۲۳۴۵۶۷۸۹"[ch]);
}

function jalaliPartsOf(gy, gm, gd) {
  const ms = Date.UTC(gy, gm - 1, gd, 12, 0, 0);
  const parts = new Intl.DateTimeFormat("en-US-u-ca-persian", {
    year: "numeric", month: "numeric", day: "numeric", timeZone: "UTC"
  }).formatToParts(new Date(ms));
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  return { jy: get("year"), jm: get("month"), jd: get("day") };
}

function cmpJalali(a, b) {
  if (a.jy !== b.jy) return a.jy - b.jy;
  if (a.jm !== b.jm) return a.jm - b.jm;
  return a.jd - b.jd;
}

export function gregorianToJalaliParts(value) {
  const d = value instanceof Date ? value : new Date(value + "T12:00:00");
  if (Number.isNaN(d.getTime())) return null;
  return jalaliPartsOf(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

export function gregorianToJalaliText(value) {
  const parts = gregorianToJalaliParts(value);
  if (!parts) return "";
  return `${parts.jy}/${String(parts.jm).padStart(2, "0")}/${String(parts.jd).padStart(2, "0")}`;
}

export const JALALI_MONTHS = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
];
export const JALALI_WEEKDAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

export function jalaliToGregorianISO(jy, jm, jd) {
  if (![jy, jm, jd].every(Number.isFinite)) return null;
  if (jm < 1 || jm > 12 || jd < 1 || jd > 31) return null;

  const DAY = 86400000;
  const anchorMs = Date.UTC(jy + 621, jm - 1, jd, 12, 0, 0);
  const target = { jy, jm, jd };

  const partsAt = (n) => {
    const dt = new Date(anchorMs + n * DAY);
    return jalaliPartsOf(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  };

  let lo = -400, hi = 400;
  while (cmpJalali(partsAt(lo), target) > 0) lo -= 400;
  while (cmpJalali(partsAt(hi), target) < 0) hi += 400;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (cmpJalali(partsAt(mid), target) < 0) lo = mid + 1;
    else hi = mid;
  }

  if (cmpJalali(partsAt(lo), target) !== 0) return null;

  const finalDate = new Date(anchorMs + lo * DAY);
  const y = finalDate.getUTCFullYear();
  const m = String(finalDate.getUTCMonth() + 1).padStart(2, "0");
  const d = String(finalDate.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function jalaliMonthLength(jy, jm) {
  for (let d = 31; d >= 29; d--) {
    if (jalaliToGregorianISO(jy, jm, d)) return d;
  }
  return 29;
}

export function jalaliFirstWeekdayIndex(jy, jm) {
  const iso = jalaliToGregorianISO(jy, jm, 1);
  if (!iso) return 0;
  const dow = new Date(iso + "T12:00:00Z").getUTCDay();
  return (dow + 1) % 7;
}

export function parseJalaliInput(text) {
  if (!text) return null;
  const normalized = toEnglishDigits(text).trim().replace(/[.-]/g, "/");
  const match = normalized.match(/^(\d{3,4})\/(\d{1,2})\/(\d{1,2})$/);
  if (!match) return null;
  return jalaliToGregorianISO(Number(match[1]), Number(match[2]), Number(match[3]));
}
